'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    brand: '',
    partDescription: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = encodeURIComponent(
      `Hello Baninvest,\n\nName: ${formData.name}\nPhone: ${formData.phone}\nVehicle Brand: ${formData.brand}\nPart Needed: ${formData.partDescription}`
    );
    window.open(`https://wa.me/256772500220?text=${msg}`, '_blank');
    setTimeout(() => {
      window.open(`https://wa.me/256757915111?text=${msg}`, '_blank');
    }, 800);
    setSubmitted(true);
  };

  return (
    <section id="contact" className="contact-section py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 reveal-hidden" data-reveal>
          <p className="text-brand-yellow text-xs font-heading font-black uppercase tracking-widest mb-3">
            Get In Touch
          </p>
          <h2 className="font-heading font-black text-white text-3xl md:text-5xl tracking-tight mb-4">
            Find Your Part Today
          </h2>
          <p className="text-white/50 font-body max-w-xl mx-auto text-sm sm:text-base">
            Tell us what you need and we&apos;ll get back to you within the hour. Available Monday–Saturday, 8am–6pm EAT.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          {/* Contact Info Cards */}
          <div className="space-y-4 sm:space-y-5 reveal-hidden" data-reveal>
            <div className="contact-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-red/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="PhoneIcon" size={22} className="text-brand-red" variant="solid" />
                </div>
                <div>
                  <p className="text-white/40 font-heading font-bold text-xs uppercase tracking-widest mb-2">Call / WhatsApp</p>
                  <a href="tel:+256772500220" className="text-white font-heading font-bold text-base sm:text-lg hover:text-brand-yellow transition-colors block">
                    +256 772 500 220
                  </a>
                  <a href="tel:+256757915111" className="text-white font-heading font-bold text-base sm:text-lg hover:text-brand-yellow transition-colors block">
                    +256 757 915 111
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-yellow/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="EnvelopeIcon" size={22} className="text-brand-yellow" variant="solid" />
                </div>
                <div>
                  <p className="text-white/40 font-heading font-bold text-xs uppercase tracking-widest mb-2">Email</p>
                  <a href="mailto:baninvestltd@gmail.com" className="text-white font-heading font-bold text-base sm:text-lg hover:text-brand-yellow transition-colors break-all">
                    baninvestltd@gmail.com
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-blue/40 flex items-center justify-center flex-shrink-0">
                  <Icon name="MapPinIcon" size={22} className="text-brand-blue-light" variant="solid" />
                </div>
                <div>
                  <p className="text-white/40 font-heading font-bold text-xs uppercase tracking-widest mb-2">Location</p>
                  <p className="text-white font-heading font-bold text-base sm:text-lg leading-snug">
                    Mengo Hill Road<br />
                    <span className="text-white/60 font-body font-normal text-sm sm:text-base">PO BOX 133230, Kampala, Uganda</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="contact-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-red/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="ClockIcon" size={22} className="text-brand-red" />
                </div>
                <div>
                  <p className="text-white/40 font-heading font-bold text-xs uppercase tracking-widest mb-2">Business Hours</p>
                  <p className="text-white font-heading font-bold text-sm sm:text-base">Mon – Sat: 8:00am – 6:00pm EAT</p>
                  <p className="text-white/50 font-body text-xs sm:text-sm">Sunday: Emergency orders via WhatsApp only</p>
                </div>
              </div>
            </div>

            {/* WhatsApp Quick Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <a
                href="https://wa.me/256772500220"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp flex-1 justify-center text-sm"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                +256 772 500 220
              </a>
              <a
                href="https://wa.me/256757915111"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp flex-1 justify-center text-sm"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                +256 757 915 111
              </a>
            </div>
          </div>

          {/* Inquiry Form */}
          <div className="reveal-hidden stagger-2" data-reveal>
            <div className="bg-white/05 border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <h3 className="font-heading font-black text-white text-lg sm:text-xl mb-6">
                Request a Part — We&apos;ll Respond Within 1 Hour
              </h3>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircleIcon" size={32} className="text-green-400" variant="solid" />
                  </div>
                  <p className="text-white font-heading font-bold text-lg">WhatsApp opened!</p>
                  <p className="text-white/50 font-body text-sm mt-2">Your inquiry has been pre-filled and sent to both WhatsApp numbers. Complete and send on WhatsApp.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-brand-yellow font-heading font-bold text-sm underline"
                  >
                    Submit another inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/50 text-xs font-heading font-bold uppercase tracking-wider block mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g. Robert Ochieng"
                        className="w-full border border-white/15 rounded-xl px-4 py-3 text-white placeholder:text-white/25 font-body text-sm focus:outline-none focus:border-brand-yellow transition-colors"
                        style={{ background: 'rgba(255,255,255,0.06)' }}
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs font-heading font-bold uppercase tracking-wider block mb-2">
                        Phone / WhatsApp *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+256 7XX XXX XXX"
                        className="w-full border border-white/15 rounded-xl px-4 py-3 text-white placeholder:text-white/25 font-body text-sm focus:outline-none focus:border-brand-yellow transition-colors"
                        style={{ background: 'rgba(255,255,255,0.06)' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-white/50 text-xs font-heading font-bold uppercase tracking-wider block mb-2">
                      Vehicle Brand
                    </label>
                    <select
                      name="brand"
                      value={formData.brand}
                      onChange={handleChange}
                      className="w-full border border-white/15 rounded-xl px-4 py-3 text-white font-body text-sm focus:outline-none focus:border-brand-yellow transition-colors"
                      style={{ background: 'rgba(255,255,255,0.06)' }}
                    >
                      <option value="" style={{ background: '#0D1117' }}>Select vehicle brand...</option>
                      <option value="Mercedes Benz" style={{ background: '#0D1117' }}>Mercedes Benz</option>
                      <option value="MAN" style={{ background: '#0D1117' }}>MAN Trucks</option>
                      <option value="Scania" style={{ background: '#0D1117' }}>Scania</option>
                      <option value="Volvo" style={{ background: '#0D1117' }}>Volvo</option>
                      <option value="BPW Trailers" style={{ background: '#0D1117' }}>BPW Trailers</option>
                      <option value="HOWO" style={{ background: '#0D1117' }}>HOWO</option>
                      <option value="Beiben" style={{ background: '#0D1117' }}>Beiben</option>
                      <option value="Other European" style={{ background: '#0D1117' }}>Other European Brand</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-white/50 text-xs font-heading font-bold uppercase tracking-wider block mb-2">
                      Part Description *
                    </label>
                    <textarea
                      name="partDescription"
                      required
                      rows={4}
                      value={formData.partDescription}
                      onChange={handleChange}
                      placeholder="e.g. Front brake pads for Mercedes Actros 2644 — need 2 sets urgently"
                      className="w-full border border-white/15 rounded-xl px-4 py-3 text-white placeholder:text-white/25 font-body text-sm focus:outline-none focus:border-brand-yellow transition-colors resize-none"
                      style={{ background: 'rgba(255,255,255,0.06)' }}
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-whatsapp justify-center py-4 text-base rounded-xl"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Send via WhatsApp
                  </button>

                  <p className="text-white/25 text-xs font-body text-center">
                    Your inquiry will be sent via WhatsApp for instant response
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}